import type { FC } from "react";

import dompurify from "isomorphic-dompurify";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import { getSessionStorage } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { DEPRECATED_Loading, Tooltip, Txt, useTranslations } from "@jti/ui";
import LoyaltyLogo from "@jti/ui/icons/loyalty_logo.svg";

import { useOrder } from "@checkout/hooks";

import "./OrderLoyaltyConfirmation.scss";

const { sanitize } = dompurify;

type OrderLoyaltyConfirmationProps = {
  isActive: boolean;
  totalPoints: number;
  pendingPoints: number;
};

export const OrderLoyaltyConfirmation: FC<OrderLoyaltyConfirmationProps> = ({
  isActive,
  totalPoints,
  pendingPoints,
}) => {
  const { __ } = useTranslations();
  const { loyalty, orderSummaryConfig } = useCheckoutConfiguration();
  const { orderFetchStatus } = useOrder();
  const ARROW_URL = loyalty?.orderConfirmationLoyaltyUrl;

  const earnedPoints = getSessionStorage().get("loyaltyCartPoints", 0);

  if (!isActive || !earnedPoints || !orderSummaryConfig?.displayLoyaltyConfirmation) {
    return null;
  }

  const dispatchGTMEvent = () => {
    dispatchTrackingEvent(TrackingEvents.TrackingPointsHistoryClick, { cta_name: "points_history" });
  };

  if (orderFetchStatus === "pending") {
    return <DEPRECATED_Loading />;
  }

  return (
    <div className="reward-info">
      <Card variant="level-1" title={__("cart.checkout.reward_banner.title")}>
        <div className={"reward-banner"}>
          <div className={"reward-banner__content"}>
            <LoyaltyLogo className={"reward-banner__logo"} />
            <Txt className={"reward-banner__message"}>
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitize(__("cart.checkout.reward_banner.message", [earnedPoints])),
                }}
                data-testid={"cart-checkout-reward_banner"}
              ></span>
            </Txt>
          </div>
        </div>
      </Card>
      <div className="reward-points">
        <Card variant="level-1-club" title={__("cart.checkout.reward_banner.points_balance.title")}>
          <Txt className="reward-points__content">
            <span className="reward-points__balance" data-testid="cart-checkout-reward_balance">
              {totalPoints}
            </span>
            <div className="reward-points__pending">
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitize(__("cart.checkout.reward_banner.points_pending", [pendingPoints])),
                }}
                className="reward-points__text"
                data-testid="cart-checkout-reward_pending-points"
              ></span>
              <Tooltip
                content={
                  <>
                    <div>{__("tooltip.text")}</div>
                    <div
                      className="reward-points__tooltip-link"
                      dangerouslySetInnerHTML={{ __html: sanitize(__("tooltip.text.url-text")) }}
                    ></div>
                  </>
                }
              />
            </div>
            <Button
              variant="text-muted"
              size="sm"
              data-testid="arrow-link"
              href={ARROW_URL}
              nativeBehaviour={true}
              onClick={dispatchGTMEvent}
              iconAfter={LongArrowForwardIcon}
            >
              {__("cart.checkout.reward_banner.points_history")}
            </Button>
          </Txt>
        </Card>
      </div>
    </div>
  );
};

export default OrderLoyaltyConfirmation;
